import { useState, useRef, useEffect } from 'react';

// Shared list windowing: render only the first `pageSize` items and lazily
// reveal `pageSize` more as the sentinel scrolls into view (IntersectionObserver)
// or when the caller's "Show more" control is clicked. Mirrors the Orders tab
// behaviour so large tables (dealers, freight lines, etc.) paint fast and
// re-render cheaply.
//
// `resetDeps` — when any of these change (filters / sort / search / data length)
// the window clamps back to the first page so the user starts at the top.
export function useWindowedList(items, { pageSize = 30, resetDeps = [] } = {}) {
  const list = items || [];
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    setVisibleCount(pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisibleCount((c) => c + pageSize);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visibleCount, pageSize]);

  const total = list.length;
  const visible = list.slice(0, visibleCount);
  const hasMore = total > visibleCount;
  const remaining = Math.max(0, total - visibleCount);
  const showMore = () => setVisibleCount((c) => c + pageSize);

  return { visible, hasMore, remaining, total, visibleCount, loadMoreRef, showMore };
}
